
import React, { useState } from 'react';
import ModelsComponent from './ModelsComponent';
import WidgetSection from './WidgetSection';
import { useComponentRegistry } from '../../contexts/ComponentRegistryContext';

type ModelSpecProps = {
  id?: string;
  selectedContainerId?: string;
  onAddComponent?: (componentId: string, componentType: string) => void;
  onAddComponentsBatch?: (submodelId: string, submodelType: string, childComponents: {id: string, type: string}[]) => void;
}

export const ModelSpec = ({ id, selectedContainerId, onAddComponent, onAddComponentsBatch }: ModelSpecProps) => {
  const baseId = id || 'model-spec';
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { isLoading, error, canUseComponent} = useComponentRegistry();

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleAddComponent = (componentId: string, componentType: string) => {
    if (!selectedContainerId) {
      console.warn('No container selected to add component to');
      return;
    }

    if (!canUseComponent(componentId, selectedContainerId)) {
      console.warn(`Component ${componentId} cannot be used in container ${selectedContainerId}`);
      return;
    }

    if (onAddComponent) {
      onAddComponent(componentId, componentType);
    }
  };

  const handleAddComponentsBatch = (submodelId: string, submodelType: string, childComponents: {id: string, type: string}[]) => {
    if (!selectedContainerId) {
      console.warn('No container selected to add components to');
      return;
    }

    if (!canUseComponent(submodelId, selectedContainerId)) {
      console.warn(`Component ${submodelId} cannot be used in container ${selectedContainerId}`);
      return;
    }

    if (onAddComponentsBatch) {
      onAddComponentsBatch(submodelId, submodelType, childComponents);
    }
  };

  if (isLoading) {
    return (
      <div id={`${baseId}-loading`} className="tw-p-4 tw-flex tw-items-center tw-justify-center">
        <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-t-2 tw-border-b-2 tw-border-blue-500"></div>
        <span className="tw-ml-2 tw-text-gray-600">Loading components...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div id={`${baseId}-error`} className="tw-p-4 tw-text-red-500 tw-bg-red-50 tw-border tw-border-red-300 tw-rounded">
        <div className="tw-font-medium">Error loading components</div>
        <div className="tw-text-sm">{error}</div>
      </div>
    );
  }

  return (
    <div id={baseId} data-testid={baseId} className="tw-w-60">
      <div id={`${baseId}-content`} className="tw-py-4 tw-px-2">
        {/* Header with search and controls */}
        <div id={`${baseId}-header`} className="tw-mb-4">
          <h2 id={`${baseId}-title`} className="tw-text-sm tw-text-[#6D788C] tw-font-semibold">Add Components</h2>

          {/* Search input */}
          <div id={`${baseId}-search-wrapper`} className="tw-mt-2">
            <input
              id={`${baseId}-search-input`}
              data-testid={`${baseId}-search-input`}
              type="text"
              placeholder="Search components..."
              aria-label="Search components"
              className="tw-w-full tw-px-4 tw-py-2 tw-bg-[#FDFDFD] tw-border tw-border-[#EFEFF0] tw-rounded tw-placeholder-[#747593] tw-text-[#747593]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Expand/Collapse toggle */}
          <div id={`${baseId}-controls`} className="tw-flex tw-items-center tw-justify-end tw-mt-1">
            <button
              id={`${baseId}-toggle-btn`}
              data-testid={`${baseId}-toggle-btn`}
              className="tw-px-2 tw-py-1 tw-font-semibold tw-text-xs tw-text-[#091CAA] tw-hover:underline"
              onClick={toggleCollapse}
              aria-label={isCollapsed ? "Expand all sections" : "Collapse all sections"}
            >
              {isCollapsed ? 'Expand All' : 'Collapse All'}
            </button>
          </div>
        </div>
        
        
        {/* Component sections with scrolling */}
        <div id={`${baseId}-sections`} className="tw-space-y-4 tw-overflow-y-auto tw-max-h-[calc(100vh-170px)] tw-pr-1 tw-scrollbar tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent">
          {!selectedContainerId ? (
            <div id={`${baseId}-no-selection`} className="tw-p-4 tw-text-center tw-text-gray-500 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded">
              Select a container to add components
            </div>
          ) : (
            <>
              {/* Models component shows submodels and their fields */}
              <ModelsComponent
                id={`${baseId}-models`}
                isCollapsed={isCollapsed}
                searchTerm={searchTerm}
                selectedContainerId={selectedContainerId}
                onAddComponent={handleAddComponent}
                onAddComponentsBatch={handleAddComponentsBatch}
              />

              {/* Widget section shows available widgets*/}
              <WidgetSection
                id={`${baseId}-widgets`}
                isCollapsed={isCollapsed}
                selectedContainerId={selectedContainerId}
                searchTerm={searchTerm}
                onAddWidget={(widgetId) => handleAddComponent(widgetId, 'widget')}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModelSpec;